/**
 * useSignupTelemetry — fires step lifecycle events to the activation funnel.
 *
 * Contract: each step component renders this hook with its step_name. The hook
 * fires `signup/step/start` on mount and returns `complete()` / `skip()`
 * functions that advance the wizard while recording the event. Time-on-step
 * is computed via performance.now() from mount to advance.
 *
 * This is the architectural guarantee that every step fires events: a step
 * component cannot skip the lifecycle by accident, because the only way to
 * advance is through the hook's returned functions.
 */
import { useEffect, useRef, useState } from 'react';
import { signupApi, StepName } from './signupApi';

interface UseSignupTelemetryReturn {
  /** Records the completion event AND persists the step payload. */
  complete: (payload: Record<string, unknown>) => Promise<void>;
  /** Records a skip event (rejected by backend if step is non-skippable). */
  skip: () => Promise<void>;
  /** Records back-navigation telemetry only — does NOT persist data. */
  goBack: (toStep: StepName) => Promise<void>;
  /** True while a network call is in flight — wire to step's submit button. */
  pending: boolean;
  /** Last error from the API, surfaced to the step UI. */
  error: string | null;
}

interface UseSignupTelemetryOptions {
  stepName: StepName;
  /** Called after `complete` / `skip` succeeds; advances the wizard. */
  onAdvance: () => void;
  /** Called after `goBack` records the event; navigates the wizard back. */
  onBack?: (toStep: StepName) => void;
}

export function useSignupTelemetry({
  stepName,
  onAdvance,
  onBack,
}: UseSignupTelemetryOptions): UseSignupTelemetryReturn {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const startedAtRef = useRef<number>(performance.now());

  // Fire `started` once on mount. Don't refire on rerenders.
  useEffect(() => {
    startedAtRef.current = performance.now();
    signupApi.start(stepName).catch((err) => {
      // Non-fatal: telemetry must never block the wizard. Log and move on.
      console.warn('[signup] step.start failed:', err);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const elapsedMs = (): number => Math.round(performance.now() - startedAtRef.current);

  const complete = async (payload: Record<string, unknown>) => {
    setPending(true);
    setError(null);
    try {
      await signupApi.complete(stepName, payload, elapsedMs());
      onAdvance();
    } catch (err: any) {
      const msg =
        err?.response?.data?.detail ?? err?.message ?? 'Could not save. Please try again.';
      setError(typeof msg === 'string' ? msg : 'Could not save. Please try again.');
    } finally {
      setPending(false);
    }
  };

  const skip = async () => {
    setPending(true);
    setError(null);
    try {
      await signupApi.skip(stepName, elapsedMs());
      onAdvance();
    } catch (err: any) {
      const msg = err?.response?.data?.detail ?? 'Could not skip this step.';
      setError(typeof msg === 'string' ? msg : 'Could not skip this step.');
    } finally {
      setPending(false);
    }
  };

  const goBack = async (toStep: StepName) => {
    // Back is telemetry-only and best-effort; we navigate even if the API call fails.
    signupApi.back(stepName, toStep).catch(() => {});
    onBack?.(toStep);
  };

  return { complete, skip, goBack, pending, error };
}
